import { type Company, type Notice, noticeDateFromValue } from "@dutyon/apis";
import { Button, DatePickerInput, Select, Stack, Textarea, TextInput } from "@dutyon/ui-kit";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

// Zod validation schema
const noticeSchema = z.object({
  title: z
    .string()
    .min(1, { message: "공지사항 제목을 입력해주세요" })
    .max(120, { message: "공지사항 제목은 120자 이하로 입력해주세요" }),
  content: z
    .string()
    .max(50_000, { message: "본문 내용은 50,000자 이하로 입력해주세요" })
    .optional(),
  imageUrl: z
    .string()
    .max(2_048, { message: "이미지 URL은 2,048자 이하로 입력해주세요" })
    .refine((value) => !value || /^https:\/\/[^\s]+$/.test(value), {
      message: "HTTPS 이미지 주소를 입력해주세요",
    })
    .optional(),
  companyId: z
    .string({ required_error: "병원을 지정해주세요" })
    .min(1, { message: "병원을 지정해주세요" }),
  dateRange: z
    .tuple([z.date().nullable(), z.date().nullable()])
    .refine((val): val is [Date, Date] => val[0] !== null && val[1] !== null, {
      message: "게시 기간을 시작일과 종료일 모두 선택해주세요",
    }),
});

interface NoticeFormData {
  title: string;
  content?: string;
  imageUrl?: string;
  companyId: string;
  dateRange: [Date | null, Date | null];
}

export interface NoticeFormPayload {
  title: string;
  content: string;
  imageUrl?: string;
  companyId: string;
  target: "APP" | "DASHBOARD";
  startDate: Date;
  endDate: Date;
}

interface NoticeFormProps {
  target: "APP" | "DASHBOARD";
  initialData?: Notice | null;
  companies: Company[];
  onSubmit: (payload: NoticeFormPayload) => void;
  loading: boolean;
  onCancel: () => void;
}

export function NoticeForm({
  target,
  initialData,
  companies,
  onSubmit,
  loading,
  onCancel,
}: NoticeFormProps) {
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<NoticeFormData>({
    defaultValues: {
      title: "",
      content: "",
      imageUrl: "",
      companyId: "SYSTEM",
      dateRange: [null, null],
    },
    resolver: zodResolver(noticeSchema),
  });

  // Synchronize form values when initialData updates (e.g. from async useQuery)
  useEffect(() => {
    if (initialData) {
      reset({
        title: initialData.title,
        content: initialData.content || "",
        imageUrl: initialData.imageUrl || "",
        companyId: initialData.companyId || "SYSTEM",
        dateRange: [
          noticeDateFromValue(initialData.startDate),
          noticeDateFromValue(initialData.endDate),
        ],
      });
    } else {
      reset({
        title: "",
        content: "",
        imageUrl: "",
        companyId: "SYSTEM",
        dateRange: [null, null],
      });
    }
  }, [initialData, reset]);

  const handleFormSubmit = (data: NoticeFormData) => {
    const [startDate, endDate] = data.dateRange;
    if (!startDate || !endDate) return;

    onSubmit({
      title: data.title,
      content: data.content || "",
      imageUrl: data.imageUrl || undefined,
      companyId: data.companyId,
      target,
      startDate,
      endDate,
    });
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
      <Stack gap="md">
        <Controller
          name="companyId"
          control={control}
          render={({ field }) => (
            <Select
              label="병원 지정"
              placeholder="공지사항을 적용할 병원 선택"
              data={[
                { value: "SYSTEM", label: "📢 시스템 전체 공지 (전체 병원 대상)" },
                ...companies.map((c) => ({ value: c.id, label: c.name })),
              ]}
              value={field.value}
              onChange={field.onChange}
              error={errors.companyId?.message}
              required
            />
          )}
        />

        <TextInput
          label="제목"
          placeholder="공지사항 제목을 입력하세요"
          required
          {...register("title")}
          error={errors.title?.message}
          maxLength={120}
        />

        <Textarea
          label="본문 내용"
          placeholder="공지사항 세부 내용을 입력하세요"
          rows={4}
          {...register("content")}
          error={errors.content?.message}
        />

        <TextInput
          label="이미지 URL"
          placeholder="https://example.com/image.png (선택)"
          {...register("imageUrl")}
          error={errors.imageUrl?.message}
          maxLength={2048}
        />

        <Controller
          name="dateRange"
          control={control}
          render={({ field }) => (
            <DatePickerInput
              type="range"
              label="게시 기간"
              placeholder="시작일 ~ 종료일"
              value={field.value}
              onChange={field.onChange}
              required
              locale="ko"
              valueFormat="YYYY년 MM월 DD일"
              error={errors.dateRange?.message}
            />
          )}
        />

        <div
          style={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "20px" }}
        >
          <Button type="button" variant="subtle" color="gray" onClick={onCancel}>
            취소
          </Button>
          <Button type="submit" loading={loading}>
            {initialData ? "수정 완료" : "공지사항 등록"}
          </Button>
        </div>
      </Stack>
    </form>
  );
}
